import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { InspectionDto, SubmitInspectionDto } from "@/types";
import { InspectionContext, InspectionModalSettings } from "./types";
import { useAuth } from "@/contexts/AuthContext";
import { encodeInspection, useSubmitInspection } from "@/services/Inspections";
import { useFinalAmountWarning } from "@/hooks/useFinalAmountWarning";
import { useGetCompanyById } from "@/services/Company";
import { useInspectionModalStore } from "@/stores/inspectionModalStore";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { getApiError, getFinalAmountError } from "@/utils/apiErrors";
import { toKg, toWeightQuantity, WeightInputValue } from "@/utils/weight";
import OrderStatusEnum from "@/constants/enums/OrderStatusEnum";
import Modal from "@/components/molecules/Modal";
import Stepper from "@/components/molecules/Stepper";
import ModalHeader from "@/components/atoms/ModalHeader";
import CTA from "@/components/atoms/CTA";
import Heading from "@/components/atoms/Heading";
import Loader from "@/components/atoms/Loader";
import Text from "@/components/atoms/Text";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import FinalAmountWarningModal from "@/components/organisms/FinalAmountWarningModal";
import { assessmentKey } from "@/components/organisms/FinalAmountWarningModal/assessmentLines";
import WeighingStep from "./steps/WeighingStep";
import DeviationStep from "./steps/DeviationStep";
import ReviewStep from "./steps/ReviewStep";
import { useInspectionContext } from "./useInspectionContext";
import { useDeviationPreview } from "./useDeviationPreview";
import {
  INSPECTION_STEPS,
  InspectionStep,
  toSubmitInspection,
  useInspectionForm,
  useInspectionSummary,
} from "./useInspectionForm";
import { hasErrorsForStep, validateInspection } from "./validation";
import styles from "./styles.module.css";

/**
 * Order Inspections: the receiving inspection of one order — weight and receipt, deviations, review —
 * followed by a confirmation. Opened through `useInspectionModalStore`.
 */
export default function InspectionModal() {
  const { t } = useTranslation();
  const orderId = useInspectionModalStore((s) => s.orderId) as number;
  const close = useInspectionModalStore((s) => s.close);
  const { currentCompany } = useAuth();
  const companyId = currentCompany?.companyId as number;

  const { context, status, inspections, isError } = useInspectionContext(orderId);
  const { data: company } = useGetCompanyById(companyId, { enabled: !!companyId });

  const settings: InspectionModalSettings = {
    isDeviationConfirmationEnabled: !!company?.isDeviationConfirmationEnabled,
  };

  // Held here rather than in the wizard: the submit refetches the order, which then reads as inspected,
  // and the check below would otherwise swap the confirmation for "already inspected".
  const [submitted, setSubmitted] = useState<SubmittedInspection | null>(null);

  // Another user may have inspected it, or it may have moved on, since the list was loaded.
  // Known from the order and its inspection alone, so it shows even when the rest fails to load.
  const notInspectable =
    status === undefined || inspections === undefined
      ? null
      : inspections.length > 0
        ? t("inspections:modal.alreadyInspected")
        : status !== OrderStatusEnum.AWAITING_INSPECTION
          ? t("inspections:modal.notAwaitingInspection")
          : null;

  return (
    <Modal
      opened
      onClose={close}
      size={760}
      closeOnClickOutside={false}
      showCloseButton
      title={
        <ModalHeader
          title={t("inspections:modal.title", { orderId })}
          svgAsset="cargo-truck-white"
          testId="inspection-modal-title"
        />
      }
    >
      {submitted ? (
        <>
          <Confirmation {...submitted} />
          <div className={styles.footer}>
            <CTA onClick={close} data-testid="inspection-close">
              {t("actions:close")}
            </CTA>
          </div>
        </>
      ) : context && !notInspectable ? (
        <InspectionWizard
          orderId={orderId}
          context={context}
          settings={settings}
          onClose={close}
          onSubmitted={setSubmitted}
        />
      ) : (
        <div className={styles.state} data-testid="inspection-modal-state">
          {isError ? (
            <Text color="error">{t("inspections:modal.loadError")}</Text>
          ) : notInspectable ? (
            <Text>{notInspectable}</Text>
          ) : (
            <Loader />
          )}
        </div>
      )}
    </Modal>
  );
}

interface SubmittedInspection {
  result: InspectionDto;
  withDeviation: boolean;
  pendingApproval: boolean;
}

interface InspectionWizardProps {
  orderId: number;
  context: InspectionContext;
  settings: InspectionModalSettings;
  onClose: () => void;
  onSubmitted: (submitted: SubmittedInspection) => void;
}

const InspectionWizard = ({ orderId, context, settings, onClose, onSubmitted }: InspectionWizardProps) => {
  const { t } = useTranslation();
  const form = useInspectionForm(context);
  const summary = useInspectionSummary(form.values, context);
  const preview = useDeviationPreview(orderId, form.values, context);
  const [stepIndex, setStepIndex] = useState(0);
  const step: InspectionStep = INSPECTION_STEPS[stepIndex];
  const isLastStep = stepIndex === INSPECTION_STEPS.length - 1;
  // Errors stay hidden until the inspector tries to move on, and are hidden again once they do.
  const [showErrors, setShowErrors] = useState(false);

  const errors = useMemo(
    () =>
      validateInspection(form.values, {
        deviationTypes: context.deviationTypes,
        deviationProducts: context.deviationProducts,
        hasContract: !!context.order.contract,
      }),
    [form.values, context]
  );
  const visibleErrors = showErrors ? errors : {};

  // Toggling the extra fractions or the deviation adds or removes rows, so their validation starts over.
  const formApi = {
    ...form,
    setMultiFraction: (on: boolean) => {
      setShowErrors(false);
      form.setMultiFraction(on);
    },
    setHasDeviation: (on: boolean) => {
      setShowErrors(false);
      form.setHasDeviation(on);
    },
  };

  // Only a prediction, from the company setting as last loaded: it labels the submit button. What the user is told
  // afterwards comes from the backend's response, as the setting may have changed or not been loaded.
  const sendsForApproval = settings.isDeviationConfirmationEnabled && summary.withDeviation;

  // The backend may answer a submit with a plausibility warning. The request it was about, with its photos already
  // encoded, is kept with it, so confirming or correcting a weight neither loses the form nor encodes again.
  const finalAmountWarning = useFinalAmountWarning<SubmitInspectionDto>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inFlight = useRef(false);
  // The photos take a while to encode. A modal closed meanwhile must not still register the receipt.
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const showSubmitError = (message: string) =>
    notifications.show(
      ErrorNotificationObj(t("inspections:notifications.submitFailedTitle"), message, "inspection-submit-error")
    );

  const { mutate: submitInspection, isPending } = useSubmitInspection({
    onSuccess: (inspection) => {
      inFlight.current = false;
      setIsSubmitting(false);
      finalAmountWarning.close();
      const pendingApproval = inspection.deviations.some(({ confirmationStatus }) => confirmationStatus === "PENDING");
      onSubmitted({ result: inspection, withDeviation: summary.withDeviation, pendingApproval });
      const message = pendingApproval
        ? t("inspections:notifications.sentForApproval", { orderId })
        : summary.withDeviation
          ? t("inspections:notifications.registeredWithDeviation", { orderId })
          : t("inspections:notifications.registered", { orderId });
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), message, "inspection-submit-success")
      );
    },
    onError: (error, { body }) => {
      inFlight.current = false;
      setIsSubmitting(false);
      // A warning is not a failure: the dialog takes over, and the form behind it stays as it is.
      if (finalAmountWarning.catchWarning(error, body)) return;

      // Any other answer ends the request the warning was about, so a dialog still open has nothing left to decide.
      finalAmountWarning.close();
      const { status, errorCode, reason } = getApiError(error);
      const finalAmountError = getFinalAmountError(error);
      showSubmitError(
        finalAmountError?.kind === "invalidQuantity"
          ? t("inspections:errors.invalidWeight")
          : finalAmountError?.kind === "invalidConfiguration"
            ? t("notifications:orders.finalAmountCheckMisconfigured")
            : errorCode === 180 || status === 409
              ? t("inspections:errors.notInspectable")
              : errorCode === 181
                ? t("inspections:errors.invalid", { reason: reason ?? t("inspections:errors.noReason") })
                : status === 403
                  ? t("inspections:errors.notReceiver")
                  : t("inspections:notifications.submitFailed")
      );
    },
  });

  // `isPending` also covers the requests the dialog sends. Until the backend has answered, the inspector can't go
  // back to the steps: a warning is about the request that was sent, and the form must still be what it was built from
  // when the dialog confirms it.
  const isBusy = isSubmitting || isPending;

  const goTo = (target: number) => {
    setShowErrors(false);
    setStepIndex(target);
  };

  const goNext = () => {
    if (hasErrorsForStep(errors, step)) {
      setShowErrors(true);
      return;
    }
    goTo(stepIndex + 1);
  };

  const submit = async () => {
    if (inFlight.current) return;
    // Earlier steps were valid when left, but send the inspector back if something changed since.
    const invalidStep = INSPECTION_STEPS.findIndex(
      (candidate) => candidate !== "review" && hasErrorsForStep(errors, candidate)
    );
    if (invalidStep !== -1) {
      setStepIndex(invalidStep);
      setShowErrors(true);
      return;
    }

    inFlight.current = true;
    setIsSubmitting(true);
    let body: SubmitInspectionDto;
    try {
      body = await encodeInspection(toSubmitInspection(form.values, context.deviationTypes));
    } catch {
      inFlight.current = false;
      if (isMounted.current) {
        setIsSubmitting(false);
        showSubmitError(t("inspections:notifications.submitFailed"));
      }
      return;
    }
    if (!isMounted.current) {
      inFlight.current = false;
      return;
    }
    submitInspection({ orderId, body, confirmationToken: null });
  };

  // What the dialog sends: the warned request as it was, with the token (confirming), or with the weights the
  // inspector corrected, which is a new request for a new check and so has no token.
  const resend = (body: SubmitInspectionDto, confirmationToken: string | null) => {
    if (inFlight.current) return;
    inFlight.current = true;
    submitInspection({ orderId, body, confirmationToken });
  };

  // The dialog names an entry by assessmentKey; the inspector knows it by its fraction.
  const warningLabels = Object.fromEntries(
    form.values.streams.flatMap((stream, index) => {
      const label = summary.streamLabel(stream.key);
      return label
        ? [[assessmentKey({ orderId, additionalStreamIndex: index === 0 ? undefined : index - 1 }), label]]
        : [];
    })
  );

  // Listed out (not built from the step name) so the i18n extractor sees every key.
  const stepLabels: Record<InspectionStep, string> = {
    weighing: t("inspections:steps.weighing"),
    deviations: t("inspections:steps.deviations"),
    review: t("inspections:steps.review"),
  };

  const stepContent = (candidate: InspectionStep) => {
    switch (candidate) {
      case "weighing":
        return <WeighingStep form={formApi} summary={summary} context={context} errors={visibleErrors} />;
      case "deviations":
        return (
          <DeviationStep orderId={orderId} form={formApi} summary={summary} errors={visibleErrors} preview={preview} />
        );
      case "review":
        return <ReviewStep form={formApi} summary={summary} settings={settings} preview={preview} />;
    }
  };

  return (
    <div className={styles.wizard}>
      <Stepper
        active={stepIndex}
        onStepClick={(target) => !isBusy && target < stepIndex && goTo(target)}
        allowNextStepsSelect={false}
        size="sm"
        completedIcon={<SVGAsset name="step-complete" />}
        classNames={{
          root: styles.stepperRoot,
          steps: styles.stepperHeader,
          content: styles.stepperContent,
          step: styles.step,
          stepIcon: styles.stepIcon,
          stepLabel: styles.stepLabel,
          separator: styles.separator,
        }}
      >
        {INSPECTION_STEPS.map((candidate) => (
          <Stepper.Step key={candidate} label={stepLabels[candidate]}>
            {stepContent(candidate)}
          </Stepper.Step>
        ))}
      </Stepper>

      <div className={styles.footer}>
        <CTA intent="secondary" onClick={onClose} data-testid="inspection-cancel">
          {t("actions:cancel")}
        </CTA>
        {stepIndex > 0 && (
          <CTA intent="secondary" onClick={() => goTo(stepIndex - 1)} disabled={isBusy} data-testid="inspection-back">
            {t("actions:back")}
          </CTA>
        )}
        {isLastStep ? (
          <CTA onClick={submit} loading={isBusy} data-testid="inspection-submit">
            {sendsForApproval ? t("inspections:actions.sendForApproval") : t("inspections:registerReceipt")}
          </CTA>
        ) : (
          <CTA onClick={goNext} data-testid="inspection-next">
            {t("actions:next")}
          </CTA>
        )}
      </div>

      <FinalAmountWarningModal
        warning={finalAmountWarning.warning}
        opened={finalAmountWarning.opened}
        labels={warningLabels}
        isPending={isPending}
        onCancel={finalAmountWarning.close}
        onConfirm={() => {
          if (finalAmountWarning.warning && finalAmountWarning.request) {
            resend(finalAmountWarning.request, finalAmountWarning.warning.confirmationToken);
          }
        }}
        onResubmit={(weights) => {
          if (finalAmountWarning.request) {
            resend(applyInspectionWeights(finalAmountWarning.request, weights, orderId), null);
          }
        }}
      />
    </div>
  );
};

/**
 * The submit body with the weights a warning dialog was corrected to. `weights` is keyed like the dialog's entries
 * (`assessmentKey`): `${orderId}:original` is the inspected order, `${orderId}:${i}` the i-th additional stream. The
 * encoded photos and everything else stay as they were, so the corrected request needs no new encoding.
 */
export const applyInspectionWeights = (
  body: SubmitInspectionDto,
  weights: Record<string, WeightInputValue>,
  orderId: number
): SubmitInspectionDto => {
  const weightFields = (key: string) => {
    const weight = weights[key];
    // An empty or unreadable weight becomes 0, which the backend rejects; the dialog doesn't offer to send one.
    return weight ? { finalWeightKg: toKg(weight) ?? 0, enteredWeight: toWeightQuantity(weight) } : {};
  };

  return {
    ...body,
    ...weightFields(assessmentKey({ orderId })),
    additionalStreams: body.additionalStreams.map((stream, index) => ({
      ...stream,
      ...weightFields(assessmentKey({ orderId, additionalStreamIndex: index })),
    })),
  };
};

interface ConfirmationProps {
  result: InspectionDto;
  withDeviation: boolean;
  pendingApproval: boolean;
}

/** Shown in place of the steps once the receipt is registered. */
const Confirmation = ({ result, withDeviation, pendingApproval }: ConfirmationProps) => {
  const { t } = useTranslation();
  const orderId = result.orderId;

  const body = pendingApproval
    ? t("inspections:confirmation.bodyPendingApproval", { orderId })
    : withDeviation
      ? t("inspections:confirmation.bodyWithDeviation", { orderId })
      : t("inspections:confirmation.body", { orderId });

  const items = [
    ...(pendingApproval
      ? [t("inspections:confirmation.sentForApproval"), t("inspections:confirmation.taskCreated")]
      : []),
    ...(result.additionalOrderIds.length
      ? [
          t("inspections:confirmation.ordersCreated", {
            count: result.additionalOrderIds.length,
            orderIds: result.additionalOrderIds.map((id) => `#${id}`).join(", "),
          }),
        ]
      : []),
  ];

  return (
    <div className={styles.confirmation} data-testid="inspection-confirmation">
      <Heading order={3} styleOrder={4} strong>
        {pendingApproval ? t("inspections:confirmation.titlePendingApproval") : t("inspections:confirmation.title")}
      </Heading>
      <Text size="sm" color="tertiary">
        {body}
      </Text>
      {items.length > 0 && (
        <div className={styles.confirmationList}>
          {items.map((item) => (
            <div key={item} className={styles.confirmationItem}>
              <SVGAsset name="document" width={14} height={14} alt="" />
              <Text size="sm">{item}</Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
